import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import {
  createSalatOrder,
  getCitiesByState,
  getLocalityByState,
  getSalatProduct,
  getStates,
} from "../../api";
import { useCart } from "../../store/cartProvider";
import ConvertToArabicNumbers from "../../utils/convert_numbers";

function SalatCheckout() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const cart = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, watch } = useForm();

  const selectedState = watch("state");

  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: () => getStates(),
    initialData: [],
  });

  const { data: cities } = useQuery({
    queryKey: ["cities", selectedState],
    queryFn: () => getCitiesByState(selectedState),
    initialData: [],
    enabled: !!selectedState,
  });

  const { data: localities } = useQuery({
    queryKey: ["locality", selectedState],
    queryFn: () => getLocalityByState(selectedState),
    initialData: [],
    enabled: !!selectedState,
  });

  const productResults = useQueries({
    queries: cart.items.map((item) => ({
      queryKey: ["salatProduct", item.productId],
      queryFn: () => getSalatProduct(item.productId),
      retry: false,
    })),
  });

  const lines = cart.items
    .map((item, index) => ({ item, product: productResults[index]?.data }))
    .filter((line) => line.product);

  const isFetchingProducts = productResults.some((r) => r.isLoading);

  const formatMoney = (value) => {
    const formatted = value.toFixed(2);
    return isRtl ? ConvertToArabicNumbers(formatted) : formatted;
  };

  const total = lines.reduce(
    (sum, line) => sum + parseFloat(line.product.price) * line.item.quantity,
    0
  );

  const onSubmit = async (formData) => {
    if (lines.length === 0) {
      toast.error(t("salatMahsool.checkout.error"));
      return;
    }

    setSubmitting(true);
    try {
      const order = await createSalatOrder({
        items: lines.map((line) => ({
          productId: line.item.productId,
          quantity: line.item.quantity,
        })),
        state: formData.state ? Number(formData.state) : undefined,
        locality: formData.locality ? Number(formData.locality) : undefined,
        city: formData.city ? Number(formData.city) : undefined,
        address: formData.address,
        notes: formData.notes,
      });
      cart.clear();
      toast.success(t("salatMahsool.checkout.success"));
      navigate(`/home/salatMahsool/confirmation/${order.id}`, { state: { order } });
    } catch (error) {
      toast.error(t("salatMahsool.checkout.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="w-full lg:px-20 px-4 py-8">
      <Helmet>
        <title>{t("salatMahsool.checkout.title")} - Mahsool</title>
      </Helmet>

      <h1 className="font-bold text-2xl lg:text-3xl mb-8 text-primary">
        {t("salatMahsool.checkout.title")}
      </h1>

      <div className="flex flex-col lg:flex-row gap-8 max-w-5xl">
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col gap-4">
          <h2 className="font-bold text-lg text-gray-700">{t("salatMahsool.checkout.deliveryAddress")}</h2>

          <select
            {...register("state")}
            className="border border-secondary rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">{t("salatMahsool.checkout.state")}</option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            {...register("city")}
            disabled={!selectedState || cities.length === 0}
            className="border border-secondary rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          >
            <option value="">{t("salatMahsool.checkout.city")}</option>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            {...register("locality")}
            disabled={!selectedState || localities.length === 0}
            className="border border-secondary rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          >
            <option value="">{t("salatMahsool.checkout.locality")}</option>
            {localities.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>

          <textarea
            {...register("address")}
            placeholder={t("salatMahsool.checkout.addressPlaceholder")}
            className="border border-gray-300 rounded-lg px-4 py-2"
            rows={3}
          />

          <textarea
            {...register("notes")}
            placeholder={t("salatMahsool.checkout.notesPlaceholder")}
            className="border border-gray-300 rounded-lg px-4 py-2"
            rows={2}
          />

          <button
            type="submit"
            disabled={submitting || isFetchingProducts || lines.length === 0}
            className="mt-2 px-6 py-3 text-lg font-bold text-white bg-primary rounded-lg shadow-md hover:bg-primary-dark hover:shadow-lg disabled:opacity-50"
          >
            {submitting ? t("salatMahsool.checkout.submitting") : t("salatMahsool.checkout.submit")}
          </button>
        </form>

        <div className="flex-1 bg-gray-50 rounded-xl p-6 border border-gray-200 h-fit">
          <h2 className="font-bold text-lg text-gray-700 mb-4">{t("salatMahsool.checkout.orderSummary")}</h2>
          {isFetchingProducts ? (
            <div className="text-gray-500">{t("common.loading")}</div>
          ) : (
            <div className="flex flex-col gap-2">
              {lines.map((line) => {
                const name = i18n.language === "en" && line.product.name_en ? line.product.name_en : line.product.name;
                const subtotal = parseFloat(line.product.price) * line.item.quantity;
                return (
                  <div key={line.item.productId} className="flex justify-between text-sm">
                    <span>
                      {name} × {line.item.quantity}
                    </span>
                    <span>
                      {formatMoney(subtotal)} {t("common.currency")}
                    </span>
                  </div>
                );
              })}
              <div className="flex justify-between font-bold text-primary border-t pt-2 mt-2">
                <span>{t("salatMahsool.cart.estimatedTotal")}</span>
                <span>
                  {formatMoney(total)} {t("common.currency")}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SalatCheckout;
